export const VERSION = '1.0.1';

export const DEFAULTS = {
    PRICE_MIN: 10000,
    PRICE_MAX: 400000,
    PRICE_STEP: 10
};

export const IDS = {
    filterForm: 'filter_form',
    floatingSelectedSubmit: 'filters-floating-selected-submit',
    floatingSubmit: 'filters-floating-submit'
};

export const TEXTS = {
    clearAll: 'Очистить все',
    apply: 'Применить'
};

export const CLASSNAMES = {
    activeButton: 'btn-dark icon icon_post icon_post-close active',
    activeFilterButton: 'btn-dark',
    desktopFilterButton: 'btn-info',
    selectedButton: 'btn icon icon_post icon_post-close mt-2 me-1',
    selectedButtonCompact: 'btn-light btn-dark',
    clearButton: 'btn btn-light icon icon_post icon_post-close mt-2',
    applyButton: 'btn btn-dark btn-arrow btn-arrow_top btn-floating btn-floating_bottom button-accept',
    disabled: 'disabled',
    invisible: 'invisible',
    visible: 'visible',
    floatingButtonSimple : 'btn-floating_reset',
    modalFooterFloating: 'modal-footer_floating'
};

/**
 * Экранировать значение из данных для подстановки в контекст СЕЛЕКТОРА:
 * `[attr="<тут>"]` и `:contains("<тут>")`. Ломают разбор только `"` и `\`.
 * Для id/class использовать нативный $.escapeSelector.
 * @param {*} v
 * @returns {string}
 */
export const escapeAttrValue = v => String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

export const ATTR = {
    filterType: 'data-filter-type',
    linkedFilter: 'data-linked-filter',
    filterId: 'data-filter-id',
    filterCount: 'data-filter-count',
    filtersCompact: 'data-filters-compact',
    filtersFull: 'data-filters-full',
    filtersSelectedCollapsed: 'data-filters-selected-collapsed',
    filterGroup: 'data-filter-group',
    filterPosition: 'data-position',
    activeFilters: 'data-active-filters',
    prefix: 'data-prefix',
    minValue: 'data-min-value',
    maxValue: 'data-max-value',
    clearAll: 'data-clear-all',
    filterLastChanged: 'data-last-changed',
};

export const SELECTORS = {
    mainContents: '.main-contents',
    mainContentGridSelected: '.main-content-grid .filter-selected',
    modalFilter: '.modal_filter',
    modalFilters: '.modal_filters',
    filterSelected: '.filter-selected',
    modalFooter: '#filters_modal .modal-footer',
    filtersModal: '#filters_modal',
    filterForm: '#filter_form',
    filterFormModal: '#filter_form_modal',
    filterFormPrice: '#filter_price_form',
    filterFormAccordions: '#filter_form .accordion-collapse',
    filterFormWrapper: '.filter-form',
    filterFormMaxValue: '#filter_form [id$="-max-value"]',
    filterOptions: '.filter-options',
    filterOptionsSubmit: '.filter-options [type="submit"]',
    filterOptionsReset: '.filter-options [type="reset"]',
    textInput: '[type="text"]',
    searchInput: '[type="search"]',
    submitInput: '[type="submit"]',
    resetInput: '[type="reset"]',
    submitResetInputs: '[type="submit"], [type="reset"]',
    productListSettings: '.product-list-settings',
    mainContentSelectedButtons: '#main-content .filter-selected .btn:not([data-bs-toggle])',
    buttonsWithoutToggle: '.btn:not([data-bs-toggle])',
    filterButtons: '.btn[data-filter-id]',
    filterInputs: 'input[data-filter-id]',
    rangeInput: '.range-input',
    rangeInputMinValue: "[id$='-min-value']",
    rangeInputMaxValue: "[id$='-max-value']",
    rangeInputs: '.range-input',
    rangeInputValue: '.range-input',
    slider: '.slider',
    sliderHandle: '.slider-handle',
    modal: '.modal',
    modalContent: '.modal-content',
    modalTitle: '.modal-title',
    accordionItem: '.accordion-item',
    accordionButtonText: '.accordion-button-text',
    formCheckLabel: '.form-check-label',
    formCheckInput: '.form-check-input',
    listGroupItem: '.list-group-item',
    mobilePriceToggle: '[data-bs-target*="price"]',
    mobileFiltersToggle: '[data-bs-target$="filters_modal"]',
    filtersCompact: '[data-filters-compact]',
    filtersFull: '[data-filters-full]',
    filterDesktopPriceMin: '#filter-desktop-price-min-value',
    filterDesktopPriceMax: '#filter-desktop-price-max-value',
    filterWrappers: '.filter-form, .modal_filter, .modal_filters',
    filterControls: '.form-control, .form-check-input, .slider-track, .btn-light',
    formControlWithPrefix: '.form-control[data-prefix]'
};

export const PRICE_RANGE_SLIDER_IDS = [
    'filter-desktop-price',
    'filter-mobile-price',
    'filter-modal-price'
];

export const ALLOWED_KEYS = [
    'Backspace',
    'Delete',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Tab'
];

export const FILTER_PARAMS = [
    {
        container: SELECTORS.mainContents,
        buttonClass: 'btn-info',
        selectedContainers: SELECTORS.mainContentGridSelected
    },
    {
        container: SELECTORS.modalFilter,
        buttonClass: 'btn-light active'
    },
    {
        container: SELECTORS.modalFilters,
        buttonClass: 'btn-light active'
    }
];

export const NUMERIC_INPUTS = [
    {
        selector: `${SELECTORS.filterOptions} ${SELECTORS.rangeInput}`,
        updateEvent: 'blur'
    },
    {
        selector: `${SELECTORS.modal} ${SELECTORS.rangeInput}`,
        updateEvent: 'blur'
    }
];

/**
 * @typedef {Object} FilterState
 * @property {number} priceMin
 * @property {number} priceMax
 * @property {number} priceStep
 * @property {Object.<string, number>} initialFilters
 * @property {Object.<string, number>} modifiedFilters
 * @property {number[]} initialPrices
 * @property {string} initialDelivery
 * @property {string} initialWarehouse
 * @property {Object.<string, any>} priceRangeSliders
 * @property {number} lastSlideButtonTime
 * @property {boolean} filterButtonInited
 */

/**
 * Создать начальное состояние фильтров.
 * @param {Partial<FilterState>=} overrides
 * @returns {FilterState}
 */
export const createFilterState = (overrides = {}) => ({
    priceMin: DEFAULTS.PRICE_MIN,
    priceMax: DEFAULTS.PRICE_MAX,
    priceStep: DEFAULTS.PRICE_STEP,
    initialFilters: {},
    modifiedFilters: {},
    initialPrices: [0, 0],
    initialDelivery: '',
    initialWarehouse: '',
    priceRangeSliders: {},
    lastSlideButtonTime: 0,
    filterButtonInited: false,
    ...overrides
});
